"use client";

import type { ProductDetails } from "@ellaandil/formatters";
import { Mail } from "@ellaandil/ui";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";
import { getDepictVariantId } from "@/lib/depict/product-id";
import { type GtmItem, variantLabel } from "@/lib/gtm";
import { useModal } from "@/lib/hooks/useModal";
import { NotifyMeModal } from "./NotifyMe";
import type { NotifyMeAlertProduct } from "./NotifyMe/schema";
import { useBuyBox } from "./useBuyBox";

type Items = ProductDetails["items"];
type Item = NonNullable<Items>[number];

export interface SizeSelectorProps {
	items: Items;
	productName: string;
	color: { name?: string | null; hex?: string };
	alertProduct: NotifyMeAlertProduct;
	/** Leaf category label for the GA4 `add_to_cart` event. */
	category?: string;
}

const inStock = (stock: Item["stock"]) => stock !== "no" && stock !== 0 && stock != null;

export const SizeSelector = ({
	items,
	productName,
	color,
	alertProduct,
	category,
}: SizeSelectorProps) => {
	const t = useTranslations();
	const { open: openNotifyMe } = useModal("notifyMe");

	const firstAvailable = useMemo(
		() => items?.find((item) => inStock(item.stock))?.name ?? items?.[0]?.name ?? null,
		[items],
	);

	const [selected, setSelected] = useState<string | null>(null);

	const selectedSize = selected ?? firstAvailable;

	const selectedItem = items?.find((item) => item.name === selectedSize);
	const isSelectedOutOfStock = selectedItem != null && !inStock(selectedItem.stock);
	const alert = {
		...alertProduct,
		size: selectedItem?.name ?? "",
		sizeSku: selectedItem?.sku ?? "",
		ean: selectedItem?.ean ?? "",
	};

	const gtmItem: GtmItem | undefined = selectedItem
		? {
				item_id: selectedItem.sku ?? alertProduct.sku,
				item_name: alertProduct.displayName,
				item_brand: alertProduct.brand || undefined,
				item_variant: variantLabel(color.name, selectedItem.name),
				item_category: category,
				price: alertProduct.priceInRegionAmount
					? Number(alertProduct.priceInRegionAmount)
					: undefined,
				quantity: 1,
			}
		: undefined;

	const { getButtonProps, getButtonLabel } = useBuyBox({
		sizes: items,
		selectedSize: selectedSize ?? undefined,
		depictProductId: getDepictVariantId(items, selectedSize),
		gtmItem,
		gtmCurrency: alertProduct.currencyInRegion,
		labels: {
			add_to_cart: t("Add to cart"),
			select_size: t("Select size"),
			out_of_stock: t("Out of stock"),
			coming_soon: t("Coming soon"),
			stock_outside_default_allocation_rule: t("Add to cart"),
		},
	});

	const hasSizes = items && items.length > 1;

	return (
		<div className="flex flex-col gap-grid-section-vertical-md">
			{hasSizes && (
				<div className="flex flex-col gap-grid-component-spacing-2xs">
					<Typography variant="label-sm-reg" className="text-text-dark-text-2">
						{t("Select size")}
					</Typography>

					<div className="flex flex-wrap gap-grid-component-spacing-3xs">
						{items.map((item) => {
							const isSelected = selectedSize === item.name;
							const isAvailable = inStock(item.stock);
							const ariaParts = [t("Size {{size}}", { size: item.name ?? "" })];
							if (!isAvailable) ariaParts.push(t("Out of stock"));
							if (isSelected) ariaParts.push(t("Selected"));

							return (
								<button
									key={item.item}
									type="button"
									onClick={() => setSelected(item.name ?? null)}
									aria-pressed={isSelected}
									aria-label={ariaParts.join(", ")}
									className="flex items-center min-h-10 rounded-radius-component-specific-button p-grid-component-spacing-2xs cursor-pointer"
								>
									<span
										className={`border-b ${isSelected ? "border-border-dark" : "border-transparent"}`}
									>
										<Typography
											variant="body-sm-reg"
											as="span"
											className={`uppercase ${isAvailable ? "text-text-dark-text-1" : "text-text-dark-text-3"}`}
										>
											{item.name}
										</Typography>
									</span>
								</button>
							);
						})}
					</div>
				</div>
			)}

			{isSelectedOutOfStock ? (
				<Button
					size="large"
					variant="outline"
					color="secondary"
					leftIcon={Mail}
					className="w-full"
					onClick={openNotifyMe}
				>
					{t("notifyMe.cta")}
				</Button>
			) : (
				<Button
					size="large"
					color="primary"
					disabled={getButtonProps().disabled}
					onClick={() => getButtonProps().onClick({} as React.MouseEvent)}
				>
					{getButtonLabel()}
				</Button>
			)}

			<NotifyMeModal productName={productName} color={color} size={selectedSize} alert={alert} />
		</div>
	);
};
